import React, { useState, useEffect } from 'react';
import { Lock, Mic, X, Play, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PodcastSection = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fallback images for podcasts without custom images
  const fallbackImages = [
    "https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=400&q=80",
    "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&q=80",
    "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&q=80",
    "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&q=80",
    "https://images.unsplash.com/photo-1460723237483-7a6dc9d0b212?w=400&q=80",
    "https://images.unsplash.com/photo-1518834107812-67b0b7c58434?w=400&q=80"
  ];

  useEffect(() => {
    loadPodcasts();
  }, []);

  const loadPodcasts = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/podcasts/active`);
      if (res.ok) {
        const data = await res.json();
        setPodcasts(data);
      }
    } catch (err) {
      console.error('Error loading podcasts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePodcastClick = (podcast) => {
    if (podcast.is_locked) {
      return; // Don't open locked podcasts
    }
    setSelectedPodcast(podcast);
  };

  const getSpotifyEmbedUrl = (podcast) => {
    // Extract episode ID from URL and create embed URL
    if (podcast.spotify_embed_id) {
      return `https://open.spotify.com/embed/episode/${podcast.spotify_embed_id}?utm_source=generator&theme=0`;
    }
    // Try to extract from full URL
    const match = podcast.spotify_url?.match(/episode\/([a-zA-Z0-9]+)/);
    if (match) {
      return `https://open.spotify.com/embed/episode/${match[1]}?utm_source=generator&theme=0`;
    }
    return null;
  };

  if (loading) {
    return (
      <section className="py-12 md:py-16 bg-[#0a0a0a]">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12">
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-white/10 rounded mb-8"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-white/5 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (podcasts.length === 0) {
    return null; // Hide section if no podcasts
  }

  return (
    <>
      <section className="py-12 md:py-16 bg-[#0a0a0a]" data-testid="podcast-section">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12">
          <h2 className="text-white text-xl md:text-2xl font-semibold tracking-wide mb-8">
            PODCASTS DE BRAVO
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {podcasts.map((podcast, index) => (
              <div
                key={podcast.id}
                onClick={() => handlePodcastClick(podcast)}
                className={`group flex items-center gap-4 p-3 rounded-lg bg-[#141414] hover:bg-[#1f1f1f] transition-colors ${
                  podcast.is_locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                }`}
                data-testid={`podcast-item-${index}`}
              >
                {/* Number */}
                <div className="text-white/20 text-2xl font-bold w-8 text-center">
                  {String(index + 1).padStart(2, '0')}
                </div>

                {/* Image */}
                <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={podcast.image_url || fallbackImages[index % fallbackImages.length]}
                    alt={podcast.title}
                    className="w-full h-full object-cover"
                  />
                  {podcast.is_locked && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-white/80" />
                    </div>
                  )}
                  {!podcast.is_locked && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all">
                      <Play className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white text-sm font-medium truncate group-hover:text-[#e4002b] transition-colors">
                    {podcast.title}
                  </h3>
                  {podcast.subtitle && (
                    <p className="text-white/60 text-xs truncate">
                      {podcast.subtitle}
                    </p>
                  )}
                  <div className="flex items-center gap-1 mt-1">
                    <Mic className="w-3 h-3 text-white/40" />
                    <span className="text-white/40 text-[10px] uppercase">
                      {podcast.is_locked ? 'Próximamente' : 'Podcast'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spotify Player Modal */}
      {selectedPodcast && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelectedPodcast(null)}
          data-testid="podcast-modal"
        >
          <div 
            className="relative w-full max-w-2xl bg-[#121212] rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1DB954] flex items-center justify-center">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">{selectedPodcast.title}</h3>
                  {selectedPodcast.subtitle && (
                    <p className="text-white/50 text-sm">{selectedPodcast.subtitle}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={selectedPodcast.spotify_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-[#1DB954] hover:bg-white/10 rounded-lg transition-colors"
                  title="Abrir en Spotify"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
                <button
                  onClick={() => setSelectedPodcast(null)}
                  className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Spotify Embed */}
            <div className="aspect-[16/9] bg-black">
              {getSpotifyEmbedUrl(selectedPodcast) ? (
                <iframe
                  src={getSpotifyEmbedUrl(selectedPodcast)}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title={selectedPodcast.title}
                  className="w-full h-full min-h-[352px]"
                ></iframe>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-white/50">No se puede cargar el reproductor</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 bg-[#1a1a1a] flex items-center justify-between">
              <p className="text-white/40 text-xs">
                Powered by Spotify
              </p>
              <a
                href={selectedPodcast.spotify_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-[#1DB954] text-white text-sm font-medium rounded-full hover:bg-[#1ed760] transition-colors"
              >
                <Play className="w-4 h-4" />
                Escuchar en Spotify
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PodcastSection;
